import React, { useState, memo } from "react";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Field } from "@progress/kendo-react-form";
import ShadowCard from "../../../components/common/ShadowCard";
import FormDateField from "../../../components/formFields/FormDateField";
import FormNumericTextField from "../../../components/formFields/FormNumericTextField";
// import FormTextField from "../../../components/formFields/FormTextField";
import { Dialog } from "@progress/kendo-react-dialogs";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import moment from "moment";
import IconComponent from "../../../components/common/IconComponent";
import { FaInfoCircle, FaExclamationTriangle } from "react-icons/fa";
import "../b2bmodule.scss";

const SummaryFieldItem = memo((fieldProps: any) => {
  const { label, formatter, minWidth = "120px", value } = fieldProps;
  return (
    <div className="summary-item" style={{ minWidth }}>
      <div className="label">{label}</div>
      <div className="value">
        {formatter ? formatter(value) : (value || "N/A")}
      </div>
    </div>
  );
});

const DurationSummaryItem = memo((nightsProps: any) => {
  const nights = Number(nightsProps.value) || 0;
  return (
    <div className="summary-item" style={{ minWidth: "150px" }}>
      <div className="label">Duration</div>
      <div className="value">
        {nights} Nights, {nights + 1} Days
      </div>
    </div>
  );
});

const ChildPaxSummary = memo((props: any) => {
  const { adults = 0, children = 0, infants = 0 } = props;
  
  const paxParts = [];
  if (adults > 0) paxParts.push(`${adults} Adults`);
  if (children > 0) paxParts.push(`${children} Children`);
  if (infants > 0) paxParts.push(`${infants} Infants`);

  return (
    <div className="summary-item" style={{ minWidth: "180px" }}>
      <div className="label">Pax</div>
      <div className="value">
        {paxParts.length > 0 ? paxParts.join(", ") : "None"}
      </div>
    </div>
  );
});



const DaysValueDisplay = memo((props: any) => (
  <div className="section-subtitle mb-0 mt-1 fw-bold text-dark" style={{ fontSize: "13px" }}>
    {(Number(props.value) || 0) + 1} Days
  </div>
));

const GRID_COLS = [{ width: "45%" }, { width: "45%" }];
const GRID_GAP = { rows: 20, cols: 20 };

interface BasicDetailsSectionProps {
  adults?: number;
  childrenCount?: number;
  infantsCount?: number;
}

const BasicDetailsSection: React.FC<BasicDetailsSectionProps> = ({
  adults = 2,
  childrenCount = 0,
  infantsCount = 0
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const toggleDialog = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  return (
    <ShadowCard className="b2b-quotation-module b2b-card mb-4 p-0">
      <div className="p-4 d-flex gap-3 align-items-start border-bottom border-light">
        <div className="hotel-icon-wrap shadow-sm">
          <IconComponent icon={FaInfoCircle} />
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <Typography.h5 className="section-title mb-0">Basic Details</Typography.h5>
          </div>
          <Typography.p className="section-subtitle mb-0 mt-1" style={{ fontSize: "13px" }}>
            Please review basic details for this quote. You can edit these details to provide a quote with different configuration, without changing the trip details.
          </Typography.p>
        </div>
      </div>

      <div className="p-4">
        <div className="d-flex flex-wrap gap-4 align-items-center">
          <Field
            name="TripDestinationName"
            label="Destination"
            component={SummaryFieldItem}
          />

          <Field
            name="TripStartDate"
            label="Start Date"
            formatter={(val: any) => (val ? moment(val).format("DD MMM, YYYY") : "N/A")}
            component={SummaryFieldItem}
          />

          <Field
            name="Nights"
            component={DurationSummaryItem}
          />

          <ChildPaxSummary
            adults={adults}
            children={childrenCount}
            infants={infantsCount}
          />

          <div className="flex-grow-1">
            <Button
              type="button"
              fillMode="outline"
              themeColor="primary"
              onClick={toggleDialog}
            >
              Edit Basic Details
            </Button>
          </div>
        </div>
      </div>

      {isDialogOpen && (
        <Dialog
          title={(
            <Typography.h5 className="section-title" style={{ margin: 0, fontSize: "16px" }}>
              Edit Quote's Basic Details
            </Typography.h5>
          )}
          onClose={toggleDialog}
          width={550}
        >
          <div className="p-3 b2b-quotation-module">
            <div className="msg-box info">
              <IconComponent icon={FaInfoCircle} className="msg-icon" />
              <div>
                <div className="msg-title">Update these details to provide a quote for different configuration without changing the trip details.</div>
                <div className="msg-text">
                  This update <span className="fw-bolder">will not</span> change the trip's basic details and is only applicable to this quote only.
                </div>
              </div>
            </div>

            <div className="msg-box warning">
              <IconComponent icon={FaExclamationTriangle} className="msg-icon" />
              <div className="msg-text">Any changes made in this quote will be reset.</div>
            </div>

            <GridLayout gap={GRID_GAP} cols={GRID_COLS}>
              <GridLayoutItem>
                <Field
                  name="TripStartDate"
                  label="Start Date"
                  component={FormDateField}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="Nights"
                  label="Number of Nights"
                  component={FormNumericTextField}
                />
                <Field
                  name="Nights"
                  component={DaysValueDisplay}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="Adults"
                  label="Number of adults"
                  component={FormNumericTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="Children"
                  label="Children"
                  component={FormNumericTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="Infants"
                  label="Infants"
                  component={FormNumericTextField}
                />
              </GridLayoutItem>
            </GridLayout>

            <div className="d-flex gap-3 mt-4">
              <Button type="button" themeColor="primary" className="px-4 fw-bold" onClick={toggleDialog}>Update Basic Details</Button>
              <Button type="button" fillMode="flat" onClick={toggleDialog} className="fw-semibold">Cancel</Button>
            </div>
          </div>
        </Dialog>
      )}
    </ShadowCard>
  );
};

export default memo(BasicDetailsSection);
